import { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';

interface CustomPaginationProperties {
  sourceItems: any[]
  pageSize: number
}

export default function useCustomPagination<T>(props: CustomPaginationProperties): {paginationControls: any, paginatedItems: T[]}{
  const [currentPage, setCurrentPage] = useState(1);
  const [createdTotalPages, setTotalPages] = useState(0)
  
  const {sourceItems, pageSize} = props

  useEffect(() => {
    const source =  sourceItems;
    const totalPages = Math.max(1, Math.ceil(source.length / pageSize));
    setTotalPages(totalPages);
    setCurrentPage(1); // Reset to first page on input change
  }, [sourceItems, pageSize]);


  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const source = sourceItems;
    return source.slice(start, start + pageSize);
  }, [sourceItems, currentPage, pageSize]);

  const pageNumbers = useMemo(() => {
    const pages: (number | 'ellipsis')[] = [];
    const visibleRange = 2;

    if (createdTotalPages <= 7) {
      for (let i = 1; i <= createdTotalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > visibleRange + 2) {
        pages.push('ellipsis');
      }

      const start = Math.max(2, currentPage - visibleRange);
      const end = Math.min(createdTotalPages - 1, currentPage + visibleRange);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < createdTotalPages - visibleRange - 1) {
        pages.push('ellipsis');
      }

      pages.push(createdTotalPages);
    }

    return pages;
  }, [createdTotalPages, currentPage]);

  const renderPageNumbers = useMemo(() => {
    return pageNumbers.map((item, idx) =>
      item === 'ellipsis' ? (
        <span key={`ellipsis-${idx}`} className="mx-1 beacon-label">...</span>
      ) : (
        <Button
          key={item}
          variant={item === currentPage ? 'success' : 'outline-secondary'}
          onClick={() => setCurrentPage(item)}
        >
          {item}
        </Button>
      )
    );
  }, [pageNumbers, currentPage]);

  const paginationControls = useMemo(() => (
    <div className="mt-3 d-flex justify-content-center align-items-center gap-2 flex-wrap">
      <Button
        variant="secondary"
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(currentPage - 1)}
      >
        Previous
      </Button>

      {renderPageNumbers}

      <Button
        variant="secondary"
        disabled={currentPage === createdTotalPages}
        onClick={() => setCurrentPage(currentPage + 1)}
      >
        Next
      </Button>
    </div>
  ), [renderPageNumbers, currentPage, createdTotalPages]);

  return { paginationControls, paginatedItems }
}